/**
 * EVLINKNET_PORTAL_API — same-origin `/api/*` (nginx → uvicorn).
 */
import { getPortalToken } from './portalSession';

function formatDetail(detail: unknown): string {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === 'object' && 'msg' in item) {
          return String((item as { msg: string }).msg);
        }
        return JSON.stringify(item);
      })
      .filter(Boolean)
      .join(' ');
  }
  if (typeof detail === 'object' && detail !== null) {
    return JSON.stringify(detail);
  }
  return String(detail);
}

export function portalApiErrorMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object') {
    const b = body as { detail?: unknown; message?: string };
    const msg = formatDetail(b.detail) || b.message;
    if (msg) return msg;
  }
  return `요청 실패 (${status})`;
}

export type PortalFetchInit = RequestInit & { auth?: boolean };

/** path must start with `/api/` */
export async function portalFetch(path: string, init: PortalFetchInit = {}): Promise<Response> {
  if (!path.startsWith('/api/')) {
    throw new Error(`portalFetch path must start with /api/: ${path}`);
  }
  const { auth = false, headers: hdrs, ...rest } = init;
  const headers = new Headers(hdrs);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (auth) {
    const t = getPortalToken();
    if (t) {
      headers.set('Authorization', `Bearer ${t}`);
    }
  }
  return fetch(path, { ...rest, headers });
}

export async function portalJson<T>(
  path: string,
  init: PortalFetchInit = {}
): Promise<{ ok: true; data: T } | { ok: false; status: number; message: string }> {
  const res = await portalFetch(path, init);
  const text = await res.text();
  let body: unknown;
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    return {
      ok: false,
      status: res.status,
      message: text.trim().startsWith('<')
        ? '서버가 HTML을 반환했습니다. /api 프록시 설정을 확인해 주세요.'
        : (text.slice(0, 280) || '서버 응답을 해석할 수 없습니다.'),
    };
  }
  if (!res.ok) {
    return { ok: false, status: res.status, message: portalApiErrorMessage(res.status, body) };
  }
  return { ok: true, data: body as T };
}
